import { Component } from 'tsdi';
import { wrapRequest } from 'wrap-request';
import { injectTSDI } from '@core/tsdi';
import { computed, makeObservable, observable } from 'mobx';

import { Api } from '@core/api';

import type { ChallengeDetailsSupportDto, CommunityChallengeCreateRequestDto } from '@core/api/ChallengeClient';
import { ChallengeStatus } from '@core/api/ChallengeClient';
import { ChallengeStore } from '@src/communities/community/challenges/challenge-form/store';

@Component
export class ChallengeDataStore {
    constructor() {
        makeObservable(this, {
            liveChallengeConfiguration: observable,
            lastCommunityId: observable,
            challengeConfiguration: computed,
            challengeId: computed,
            communityId: computed,
            disabledPresentationEdit: computed,
            disabledRulesEdit: computed,
            disabledConsentsEdit: computed,
            disabledRewardEdit: computed
        });
    }

    private get challengeStore() {
        return injectTSDI(ChallengeStore);
    }

    public initializeWithParams(id?: number, communityId?: number) {
        if (communityId) {
            this.lastCommunityId = communityId;
        }
        if (!id) {
            this.liveChallengeConfiguration = undefined;

            this.challengeConfigurationRequest.reset();
            this.challengeStore.resetForms();
        }
        this.loadChallenge(id);
    }

    private get challengeClient() {
        return injectTSDI(Api).challengeClient;
    }

    public challengeConfigurationRequest = wrapRequest(async (id?: number) => {
        if (!id) {
            return undefined;
        }
        const result =
            await this.challengeClient?.support.challengeSupportControllerGetDetails(
                id
            );
        this.liveChallengeConfiguration = result?.data;

        return result?.data;
    });
    public createChallengeRequest = async (
        challenge: CommunityChallengeCreateRequestDto
    ) => {
        const result =
            await this.challengeClient?.support.challengeSupportControllerCreate(
                challenge
            );
        this.liveChallengeConfiguration = result?.data;
        return result?.data;
    };

    private loadChallenge(id?: number) {
        this.challengeConfigurationRequest.request(id);
    }
    public get challengeConfiguration():
        | ChallengeDetailsSupportDto
        | undefined {
        return this.challengeConfigurationRequest.$;
    }
    public liveChallengeConfiguration: ChallengeDetailsSupportDto | undefined;
    public lastCommunityId: number | undefined;

    public get communityId(): number | undefined {
        return this.lastCommunityId;
    }

    public get challengeId() {
        return this.liveChallengeConfiguration?.id;
    }

    public get challengeStatus() {
        return this.liveChallengeConfiguration?.status;
    }

    public get disabledPresentationEdit(): boolean {
        return this.shouldDisablePresentationEdit(
            this.liveChallengeConfiguration?.status
        );
    }

    private shouldDisablePresentationEdit(status?: ChallengeStatus): boolean {
        if (status == undefined) {
            return false;
        }
        return (
            status === ChallengeStatus.CANCELED ||
            status === ChallengeStatus.FINISHED ||
            this.challengeStore.isUpdating
        );
    }

    public get disabledRulesEdit(): boolean {
        return this.shouldDisableRulesEditCheck(
            this.liveChallengeConfiguration?.status
        );
    }

    shouldDisableRulesEditCheck(status?: ChallengeStatus): boolean {
        return (
            status === ChallengeStatus.IN_PROGRESS ||
            status === ChallengeStatus.FINISHED ||
            status === ChallengeStatus.CANCELED ||
            this.challengeStore.isUpdating
        );
    }

    public get disableGroupRulesEdit(): boolean {
        return this.shouldDisableGroupRulesEditCheck(
            this.liveChallengeConfiguration?.status
        );
    }

    private shouldDisableGroupRulesEditCheck(
        status?: ChallengeStatus
    ): boolean {
        return (
            status === ChallengeStatus.FINISHED ||
            status === ChallengeStatus.CANCELED ||
            this.challengeStore.isUpdating
        );
    }

    public get disabledConsentsEdit(): boolean {
        return this.shouldDisableConsentsEditCheck(
            this.liveChallengeConfiguration?.status
        );
    }

    private shouldDisableConsentsEditCheck(
        status?: ChallengeStatus
    ): boolean {
        return (
            status === ChallengeStatus.FINISHED ||
            status === ChallengeStatus.CANCELED ||
            this.challengeStore.isUpdating
        );
    }

    public get disabledRewardEdit(): boolean {
        const status = this.liveChallengeConfiguration?.status;
        if (status == undefined) {
            return false;
        }
        return (
            status === ChallengeStatus.IN_PROGRESS ||
            status === ChallengeStatus.CANCELED ||
            status === ChallengeStatus.FINISHED ||
            this.challengeStore.isUpdating
        );
    }
}
